import { useMemo } from 'react';
import styled from 'styled-components';

import { Divider } from '../../atoms/divider/Divider';
import { ListItem, type ListItemProps } from '../../atoms/list-item/ListItem';

export type SectionProps = {
	hideListItemDividers?: boolean;
	items: Array<ListItemProps>;
	testID?: string;
	title?: string;
};

/**
 * Section that combines a title with a list of listItems.
 *
 * @param {SectionProps} props - The props of the component
 * @param {string} props.title The section title
 * @param {string} props.items The items arrays
 * @param {string} props.testID To locate the element in the testing environment
 *
 * @returns {JSX.Element} The Section
 */
export const Section = ({ hideListItemDividers = false, items, testID, title }: SectionProps): JSX.Element => {
	const sectionItems = useMemo(
		() =>
			items.map((item, index) => (
				<div key={`${title}-${item.title}`}>
					{index !== 0 && !hideListItemDividers && <Divider orientation="horizontal" />}
					<ListItem {...item} />
				</div>
			)),
		[items, title, hideListItemDividers]
	);

	return (
		<S.Container data-testid={testID}>
			{title && (
				<>
					<S.SimpleList>
						<S.Title>{title}</S.Title>
					</S.SimpleList>
					<Divider orientation="horizontal" />
				</>
			)}
			{sectionItems}
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
		margin-bottom: ${({ theme }) => theme.spacings.small};
		overflow: hidden;
	`;

	export const SimpleList = styled.div`
		padding: ${({ theme }) => theme.spacings.small};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Title = styled.span`
		${({ theme }) => theme.fonts.text.text1};
	`;
}
